'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

import { EXTERNAL_LINKS } from '@/configs/links';
import { Link } from '@/i18n/routing';

type FooterLink = { label: string; href: string };
type FooterColumn = { title: string; links: FooterLink[] };

export const Footer: React.FC = () => {
  const t = useTranslations('Footer');
  const columns = t.raw('columns') as FooterColumn[];
  const email = t('email');
  const year = new Date().getFullYear();

  return (
    <footer className="footer-section bg-section-a flex w-full flex-col items-center">
      <div className="flex w-full max-w-[1440px] flex-col gap-[40px] px-4 py-[40px] md:flex-row md:justify-between md:gap-[60px] md:px-[80px] md:py-[60px]">
        <div className="flex max-w-[320px] flex-col gap-[16px]">
          <Link
            href="/"
            className="flex items-center"
            aria-label="NexHiro home"
          >
            <Image
              src="/teal-wordmark.png"
              alt="NexHiro"
              width={176}
              height={28}
              className="h-[28px] w-auto"
            />
          </Link>
          <p className="font-matter text-text-secondary text-[15px] leading-[24px]">
            {t('tagline')}
          </p>
          <a
            href={`mailto:${email}`}
            className="font-matter text-text-secondary hover:text-brand-primary w-fit text-[15px] transition-colors"
          >
            {email}
          </a>
        </div>

        <div className="grid grid-cols-2 gap-x-[36px] gap-y-[32px] sm:grid-cols-4 lg:gap-x-[40px]">
          {columns.map((column) => (
            <div key={column.title} className="flex flex-col gap-[14px]">
              <span className="font-matter text-text-muted text-[13px] font-semibold tracking-[0.06em] uppercase">
                {column.title}
              </span>
              <nav className="flex flex-col gap-[12px]">
                {column.links.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="font-matter text-text-primary hover:text-brand-primary text-[15px] font-normal transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          ))}
        </div>
      </div>

      <div className="border-divider w-full max-w-[1440px] border-t pb-8 md:pb-0">
        <div className="flex w-full flex-col-reverse items-center justify-between gap-4 px-4 py-[20px] md:flex-row md:gap-0 md:px-[80px]">
          <p className="font-matter text-text-secondary text-center text-[14px] leading-[24px] font-normal md:text-start md:text-[16px]">
            {t('copyright', { year })}
          </p>

          <div className="flex items-center gap-[8px]">
            <a
              href={EXTERNAL_LINKS.TWITTER}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('social.x')}
              className="bg-brand-primary text-primary-foreground flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-full transition-all hover:opacity-90"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-[14px] w-[14px] fill-current"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href={EXTERNAL_LINKS.LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('social.linkedin')}
              className="bg-brand-primary text-primary-foreground flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-full transition-all hover:opacity-90"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-[14px] w-[14px] fill-current"
              >
                <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.25 6.5 1.75 1.75 0 016.5 8.25zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.3.96 3.3 3.45z" />
              </svg>
            </a>
            <a
              href={EXTERNAL_LINKS.GITHUB}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('social.github')}
              className="bg-brand-primary text-primary-foreground flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-full transition-all hover:opacity-90"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-[14px] w-[14px] fill-current"
              >
                <path d="M12 2C6.48 2 2 6.59 2 12.25c0 4.53 2.87 8.37 6.84 9.73.5.1.68-.22.68-.49v-1.9c-2.78.62-3.37-1.22-3.37-1.22-.45-1.18-1.11-1.49-1.11-1.49-.91-.64.07-.63.07-.63 1 .07 1.53 1.06 1.53 1.06.9 1.56 2.35 1.11 2.92.85.09-.66.35-1.11.63-1.37-2.22-.26-4.55-1.14-4.55-5.06 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.71 0 0 .84-.28 2.75 1.05A9.33 9.33 0 0112 6.98c.85 0 1.7.12 2.5.34 1.9-1.33 2.74-1.05 2.74-1.05.55 1.41.2 2.45.1 2.71.64.72 1.03 1.63 1.03 2.75 0 3.93-2.34 4.8-4.57 5.05.36.32.68.94.68 1.9v2.81c0 .27.18.59.69.49A10.12 10.12 0 0022 12.25C22 6.59 17.52 2 12 2z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
